/**
 * TOSS Web Application — Client portal: the floating action button (phone only)
 *
 * A round button in the bottom-right corner, above the bottom bar. On a phone the screen's own
 * action belongs under the thumb, not at the top of a page that scrolls away from it.
 *
 * **Phone only, and that is the point.** On a desktop the same action is a normal button in
 * the page header: there is no thumb, the cursor reaches the top of the page as easily as
 * the bottom, and a circle floating over a wide layout is a mobile idiom borrowed where it
 * buys nothing. `ClientLayout` mounts the create-application one only when the bottom bar is
 * showing, and a page that mounts its own is expected to ask `useClientViewport` the same
 * question.
 *
 * **Where it is NOT shown.** The create form itself (a button that opens the screen you are
 * on), and the CONVERSATION — there the bottom edge belongs to the message input. The list of
 * conversations does mount one («Новый чат», 29.08.2026): it is a screen with a creating action
 * like any other, and until then it was the one place in the cabinet where that action stood in
 * the top row. `clientRouteRegistry` decides; these components only draw.
 *
 * **The offset is read off the route, not passed in.** On a screen that keeps the bottom bar the
 * button clears its 64 px plus the iOS home indicator; on a full-screen route there is no bar to
 * clear and the same 64 px would leave the button hovering in the middle of nothing. Which of the
 * two a route is, is already an answer the registry gives (`isFullScreenPhonePath`), so the button
 * asks it rather than taking a prop a caller could set to disagree with the layout around it.
 *
 * Two exports, because there turned out to be two kinds of floating button:
 *
 *  - {@link ClientFab} — the shell. Takes `to` for a link and `onClick` for an action.
 *  - {@link ClientCreateFab} — «новая заявка», the one `ClientLayout` mounts from the route
 *    table. Unchanged in look and behaviour; it is now the shell plus one address.
 *
 * «Сотрудники» mounts the shell itself rather than being listed here, and the reason is in
 * `clientRouteRegistry` under `ClientFabOwner`: its action opens a form that lives in the address
 * as an OBJECT, and a bare link to that address closes itself on the first effect.
 */

import { Link, useLocation } from 'react-router-dom';
import { Plus, type LucideIcon } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { isFullScreenPhonePath } from './clientRouteRegistry';

// ============================================================================
// Types
// ============================================================================

export interface ClientFabProps {
  /** Read out by a screen reader — the button carries an icon and no text. */
  label: string;
  /** The glyph. Defaults to «+», which is what both current buttons mean. */
  icon?: LucideIcon;
  /** Navigates. Mutually exclusive with `onClick`. */
  to?: string;
  /** Acts. Mutually exclusive with `to`. */
  onClick?: () => void;
  className?: string;
}

export interface ClientCreateFabProps {
  className?: string;
}

// ============================================================================
// Shell
// ============================================================================

const FAB_CLASS = `
  fixed right-[max(1rem,env(safe-area-inset-right,0px))] z-[var(--z-sticky)]
  flex items-center justify-center
  w-14 h-14 rounded-full
  bg-[var(--color-primary-500)] text-[var(--color-text-on-primary)]
  shadow-[var(--shadow-overlay)]
  transition-colors duration-150
  hover:bg-[var(--color-primary-600)] active:bg-[var(--color-primary-700)]
  focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-2
`;

/**
 * The shell. Elevation stays here and is not a leftover of the flat-card decision of
 * 26.08.2026: this button floats OVER the content by design, and `--shadow-overlay` is the
 * role that says so.
 */
export function ClientFab({
  label,
  icon: Icon = Plus,
  to,
  onClick,
  className = '',
}: ClientFabProps): JSX.Element {
  const { pathname } = useLocation();
  // 64 px of bottom bar to clear, or none — see the docblock.
  const bottom = isFullScreenPhonePath(pathname)
    ? 'calc(env(safe-area-inset-bottom, 0px) + 16px)'
    : 'calc(64px + env(safe-area-inset-bottom, 0px) + 16px)';
  const style = { bottom };
  const content = <Icon className="w-7 h-7" aria-hidden="true" />;

  if (to) {
    return (
      <Link to={to} aria-label={label} className={`${FAB_CLASS} ${className}`} style={style}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`${FAB_CLASS} ${className}`}
      style={style}
    >
      {content}
    </button>
  );
}

// ============================================================================
// «Новая заявка»
// ============================================================================

export function ClientCreateFab({ className = '' }: ClientCreateFabProps): JSX.Element {
  return (
    <ClientFab label="Новая заявка" to={ROUTES.client.applications.create} className={className} />
  );
}

export default ClientCreateFab;
